import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _keys from "lodash/keys";
import _values from "lodash/values";
import _last from "lodash/last";
import _isString from "lodash/isString";
import _isNil from "lodash/isNil";
import "core-js/modules/es6.regexp.split.js";
import "core-js/modules/es6.array.slice.js";
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { colors } from '@nerdwallet/base-styles/variables';
import { FORMATS, VALUE_SIZES } from '@nerdwallet/marketplace/constants/drivers';
import { formatValue } from '@nerdwallet/marketplace/utils/format';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { Tooltip, Type } from '@nerdwallet/currency';
import styles from './quick-facts.module.less';
import { isServer } from './mappings/utils';
/**
 * Preserve exports for backwards compatibility.
 * @deprecated - Leverage exports from `@nerdwallet/marketplace/utils/format` directly.
 */

export { formatValue };

var QuickFactValue = function QuickFactValue(_ref) {
  var analytics = _ref.analytics,
      descriptionColorOverride = _ref.descriptionColorOverride,
      enableAnalytics = _ref.enableAnalytics,
      quickFact = _ref.quickFact,
      showNAForEmptyValue = _ref.showNAForEmptyValue;
  useDeprecatedPropsWarning({
    component: 'QuickFactValue',
    deprecatedProps: [{
      version: '5.5.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics prop instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });

  if (_isNil(quickFact.value) || quickFact.value === '') {
    return showNAForEmptyValue ? /*#__PURE__*/React.createElement(Type, null, "N/A") : null;
  }

  var formattedValue = quickFact.valueFormat === FORMATS.UNFORMATTED ? quickFact.value : formatValue(quickFact.value, quickFact.valueFormat);
  var tooltip = quickFact.valueTooltip && /*#__PURE__*/React.createElement("span", {
    className: styles.tooltipPreserveWhitespace
  }, /*#__PURE__*/React.createElement(Tooltip, {
    id: quickFact.id,
    className: styles.tooltip,
    content: /*#__PURE__*/React.createElement("span", {
      className: styles.tooltipContent
    }, !isServer() && quickFact.valueTooltip),
    title: "More information about ".concat(quickFact.label),
    placement: "bottom",
    analytics: (enableAnalytics || analytics) && _objectSpread({
      entityNamePrefix: "quick_fact_value_tooltip"
    }, analytics)
  })); // To ensure that the tooltip does not wrap to the next line,
  // wrap it in a nowrap span with the last word of the value.
  // Unfortunately, we can only do this is if the value is a string.

  var value;

  if (tooltip && _isString(formattedValue)) {
    var valueWords = formattedValue.split('');
    value = /*#__PURE__*/React.createElement(Fragment, null, valueWords.slice(0, valueWords.length - 1).join(''), /*#__PURE__*/React.createElement("span", {
      className: styles.tooltipWrapper
    }, _last(valueWords), tooltip));
  } else {
    value = /*#__PURE__*/React.createElement(Fragment, null, formattedValue, tooltip);
  }

  return /*#__PURE__*/React.createElement(Fragment, null, quickFact.valueFormat === FORMATS.UNFORMATTED ? value : /*#__PURE__*/React.createElement(Type, {
    className: styles[quickFact.valueSize || VALUE_SIZES.LARGE]
  }, value), quickFact.description && /*#__PURE__*/React.createElement(Type, {
    className: styles.description,
    color: descriptionColorOverride,
    size: 1
  }, quickFact.description));
};

QuickFactValue.propTypes = {
  /** Analytics Props */
  analytics: PropTypes.shape({
    entityName: PropTypes.string,
    parentPositionAttributes: PropTypes.shape({
      payload: PropTypes.shape({
        sectionName: PropTypes.string,
        parentImpressionId: PropTypes.string,
        sectionPosition: PropTypes.number
      })
    })
  }),

  /**
   * Flag to enable bedrock analytics
   */
  enableAnalytics: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    impressionId: PropTypes.string
  })]),

  /** Quick fact to display */
  quickFact: PropTypes.shape({
    /** The unique id for this particular quick fact */
    id: PropTypes.string.isRequired,

    /** The quick fact label text */
    label: PropTypes.node,

    /** The popover text to be associated with the label */
    labelTooltip: PropTypes.node,

    /** The quick fact value */
    value: PropTypes.node,

    /** Specifies how the value should be formatted.
     Supports 'currency', 'currencyWithCents', 'percent', 'string', and 'unformatted'.
     Defaults to 'string'. */
    valueFormat: PropTypes.oneOf(_values(FORMATS)),

    /** The popover text to be associated with the value */
    valueTooltip: PropTypes.node,

    /** The type size of the value. Supports 'large', 'medium', and 'small'.
     Defaults to 'large'. */
    valueSize: PropTypes.oneOf(_values(VALUE_SIZES)),

    /** The quick fact description */
    description: PropTypes.node
  }).isRequired,

  /** Overrides the default description color, which is 'neutral-darker' */
  descriptionColorOverride: PropTypes.oneOf(_keys(colors)),

  /** If true, an empty value will be display as 'N/A'. Otherwise, nothing will be rendered.
   Defaults to false. */
  showNAForEmptyValue: PropTypes.bool
};
QuickFactValue.defaultProps = {
  analytics: {},
  descriptionColorOverride: 'neutral-darker',
  showNAForEmptyValue: false
};
export default QuickFactValue;